import { useState } from 'react'
import { useTheme } from '../../theme'
import { useAuth } from '../../AuthContext'
import { api } from '../../api'
import RinaContextCard from '../../components/RinaContextCard'
import { classeChamp } from './champs'
import { useLocation } from 'wouter'
import PageHub from '../../components/PageHub'
import { ROUTES } from '../../routes'

/**
 * Wizard « Changer mon PIN » (3 etapes + succes, extrait en S54).
 * Etat LOCAL : entrer dans la vue monte le wizard a neuf (etape 0, champs
 * vides) — plus de reset manuel a l'ouverture.
 * Le PIN actuel peut faire 4 ou 6 chiffres (retrocompat) ; le nouveau fait
 * exactement 6 (DTO backend). `onRetourSecurite` : sortie apres succes.
 */
function WizardPin({ onRetourSecurite }) {
  const { isWeb3 } = useTheme()
  const { token } = useAuth()
  const tc = 'text-ink'
  const ts = 'text-ink/60'
  const cardBg = 'bg-card border-card-border'
  const inputCls = classeChamp(isWeb3)

  const [pinStep, setPinStep] = useState(0) // 0=actuel, 1=nouveau, 2=confirmer, 3=succes
  const [pinValues, setPinValues] = useState({ current: '', new1: '', new2: '' })
  const [pinError, setPinError] = useState('')
  const [pinLoading, setPinLoading] = useState(false)

  const handlePinNext = async () => {
    const val = pinStep === 0 ? pinValues.current : pinStep === 1 ? pinValues.new1 : pinValues.new2
    if (pinStep === 0 && (!val || val.length < 4)) { setPinError('Le PIN doit faire au moins 4 chiffres'); return }
    if (pinStep > 0 && (!val || val.length !== 6)) { setPinError('Le nouveau PIN doit faire 6 chiffres'); return }

    if (pinStep === 0) {
      // Etape 1 → 2 (on valide le PIN actuel au submit final)
      setPinStep(1)
      setPinError('')
    } else if (pinStep === 1) {
      if (val === pinValues.current) { setPinError('Le nouveau PIN doit etre different'); return }
      setPinStep(2)
      setPinError('')
    } else {
      if (val !== pinValues.new1) { setPinError('Les PIN ne correspondent pas'); setPinValues(p => ({ ...p, new2: '' })); return }
      setPinLoading(true)
      try {
        await api.users.changePin(pinValues.current, pinValues.new1, token)
        setPinStep(3)
        setPinError('')
      } catch (err) {
        setPinError(err.message || 'Erreur lors du changement de PIN')
        setPinStep(0)
        setPinValues({ current: '', new1: '', new2: '' })
      } finally {
        setPinLoading(false)
      }
    }
  }

  const stepLabels = ['Actuel', 'Nouveau', 'Confirmer']

  return (
    <div className="animate-fade-up">
      <RinaContextCard title="Changer mon PIN" message="Choisissez un code à 6 chiffres — il protège votre compte." />

      {pinStep < 3 && (
        <div className={`p-6 rounded-2xl border ${cardBg}`}>
          {/* Indicateur d'etapes */}
          <div className="flex justify-center gap-3 mb-6">
            {stepLabels.map((s, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center text-white"
                  style={{
                    background: pinStep === i ? 'rgb(var(--c-primary))' : pinStep > i ? 'rgb(var(--c-teal))' : (isWeb3 ? '#252d38' : '#ede4db'),
                    color: pinStep >= i ? '#fff' : '#8b949e',
                  }}>
                  {pinStep > i ? '✓' : i + 1}
                </div>
                <span className={`text-[10px] font-medium ${pinStep === i ? tc : ts}`}>{s}</span>
              </div>
            ))}
          </div>

          {/* Icone + titre */}
          <div className="text-center mb-5">
            <div className="text-4xl mb-2 flex justify-center">
              {pinStep === 0 ? '🔒' : pinStep === 1 ? '🔑' : <span className="badge-check" style={{ width: 48, height: 48, fontSize: 24 }}>✓</span>}
            </div>
            <p className={`text-[15px] font-semibold ${tc}`}>
              {pinStep === 0 ? 'PIN actuel' : pinStep === 1 ? 'Nouveau PIN' : 'Confirmer le PIN'}
            </p>
            <p className={`text-[12px] mt-1 ${ts}`}>
              {pinStep === 0 ? 'Entre ton code actuel' : pinStep === 1 ? 'Choisis un nouveau code à 6 chiffres' : 'Retape ton nouveau PIN'}
            </p>
          </div>

          {/* Input PIN */}
          <label htmlFor="change-pin" className="sr-only">Code PIN</label>
          <input
            id="change-pin"
            type="password"
            inputMode="numeric"
            maxLength={6}
            placeholder="••••••"
            value={pinStep === 0 ? pinValues.current : pinStep === 1 ? pinValues.new1 : pinValues.new2}
            onChange={e => {
              setPinError('')
              const val = e.target.value.replace(/\D/g, '')
              const key = pinStep === 0 ? 'current' : pinStep === 1 ? 'new1' : 'new2'
              setPinValues(p => ({ ...p, [key]: val }))
            }}
            className={`${inputCls} text-center text-xl font-mono tracking-[0.3em] mb-2`}
            style={pinError ? { borderColor: '#f85149' } : {}}
            disabled={pinLoading}
          />

          {pinError && (
            <p className="text-[12px] text-center font-medium mb-2" style={{ color: '#f85149' }}>{pinError}</p>
          )}

          <button onClick={handlePinNext} disabled={pinLoading}
            className="w-full py-3 rounded-xl text-white text-[14px] font-bold mt-2 disabled:opacity-50 flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, rgb(var(--c-primary)), rgb(var(--c-primary-soft)))' }}>
            {pinLoading
              ? <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              : pinStep === 2 ? 'Confirmer' : 'Suivant →'}
          </button>
        </div>
      )}

      {pinStep === 3 && (
        <div className={`p-8 rounded-2xl border text-center animate-fade-up ${cardBg}`}>
          <div className="mb-3 flex justify-center">
            <span className="badge-check" style={{ width: 56, height: 56, fontSize: 28 }}>✓</span>
          </div>
          <p className={`text-lg font-bold mb-1.5 ${tc}`}>PIN modifié !</p>
          <p className={`text-[13px] mb-6 leading-relaxed ${ts}`}>Ton nouveau code à 6 chiffres est actif.</p>
          <button onClick={onRetourSecurite}
            className="px-8 py-3 rounded-xl text-white text-[14px] font-bold"
            style={{ background: 'linear-gradient(135deg, rgb(var(--c-primary)), rgb(var(--c-primary-soft)))' }}>
            Retour à la sécurité
          </button>
        </div>
      )}
    </div>
  )
}

/** Page « Changer mon PIN » (S56) : une vraie Route (/account/securite/pin). */
export default function PageChangerPin() {
  const [, setLocation] = useLocation()
  const retourSecurite = () => setLocation(ROUTES.compte.securite)
  return (
    <PageHub retour={{ label: 'Sécurité', vers: ROUTES.compte.securite }}>
      <WizardPin onRetourSecurite={retourSecurite} />
    </PageHub>
  )
}
